import type { ChildId } from './valueObjects/childId';
import type { LeaveEarlyDateTime } from './valueObjects/leaveEarlyDateTime';
import type { Memo } from './valueObjects/memo';
import type { NurseryId } from './valueObjects/nurseryId';
import type { ParentId } from './valueObjects/parentId';
import type { AttendContactLeaveEarlyJsonPub, AttendContactLeaveEarlyJsonPvt } from '@/types/domain/attendContact/entities/attendContactLeaveEarlyEntity';

import {
  EntityPub,
} from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * 早退連絡プロパティ非公開
 */
export type AttendContactLeaveEarlyPropsPvt = {
  /** 施設ID */
  nurseryId: NurseryId,
  /** 親ID */
  parentId: ParentId,
  /** 子ID */
  childId: ChildId,
  /** 早退日時 */
  leaveEarlyDateTime: LeaveEarlyDateTime,
  /** メモ */
  memo: Memo,
}

/**
 * 早退連絡プロパティ公開
 */
export type AttendContactLeaveEarlyPropsPub = Record<string, never>

/**
 * 早退連絡Entity
 */
export class AttendContactLeaveEarlyEntity extends EntityPub<
  AttendContactLeaveEarlyPropsPvt,
  AttendContactLeaveEarlyJsonPvt,
  AttendContactLeaveEarlyPropsPub,
  AttendContactLeaveEarlyJsonPub
> {
  get deleteType(): DeleteType {
    return DeleteType.Dt;
  }

  get deleteKey(): string {
    return this.getFutureYear(this.leaveEarlyDateTime.value, 1).toJSON();
  }

  toJsonPubData(): AttendContactLeaveEarlyJsonPub {
    return {};
  }

  toJsonData(): AttendContactLeaveEarlyJsonPvt {
    const p: AttendContactLeaveEarlyJsonPvt = {
      nurseryId: this.nurseryId.value,
      parentId: this.parentId.value,
      childId: this.childId.value,
      leaveEarlyDateTime: this.leaveEarlyDateTime.value.toJSON(),
      memo: this.memo.value,
    };
    return p;
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoNurseryAttendContactAttendContactLeaveEarlyEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    const ymd = this.leaveEarlyDateTime.toStringDate();
    return `${ymd}#${this.parentId.value}_${this.childId.value}#${this.nurseryId.value}`;
  }

  get subKey(): string | null {
    const ymd = this.leaveEarlyDateTime.toStringDate();
    return `${this.parentId.value}_${this.childId.value}#${ymd}#${this.nurseryId.value}`;
  }

  /** 施設ID */
  get nurseryId(): NurseryId {
    return this._value.nurseryId;
  }

  /** 施設ID */
  set nurseryId(nurseryId: NurseryId) {
    this._value.nurseryId = nurseryId;
  }

  /** 親ID */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /** 親ID */
  set parentId(parentId: ParentId) {
    this._value.parentId = parentId;
  }

  /** 子ID */
  get childId(): ChildId {
    return this._value.childId;
  }

  /** 子ID */
  set childId(childId: ChildId) {
    this._value.childId = childId;
  }

  /** 早退日時 */
  get leaveEarlyDateTime(): LeaveEarlyDateTime {
    return this._value.leaveEarlyDateTime;
  }

  /** 早退日時 */
  set leaveEarlyDateTime(leaveEarlyDateTime: LeaveEarlyDateTime) {
    this._value.leaveEarlyDateTime = leaveEarlyDateTime;
  }

  /** メモ */
  get memo(): Memo {
    return this._value.memo;
  }

  /** メモ */
  set memo(memo: Memo) {
    this._value.memo = memo;
  }
}
